import { canonicalListingTemplatePlatform } from './listingTemplatePlatformUtils';

let xlsxModulePromise = null;

export async function loadListingTemplateXLSX() {
  if (!xlsxModulePromise) {
    xlsxModulePromise = import('xlsx');
  }
  const mod = await xlsxModulePromise;
  return mod.default || mod;
}

/** 解析 SHEIN / TEMU 等模板时的 XLSX.read 选项 */
export const LISTING_TEMPLATE_XLSX_READ_OPTIONS = {
  type: 'array',
  cellStyles: true,
  cellNF: true,
  cellHTML: false,
  sheetStubs: true,
};

/** 各平台「模版」工作表中数据区起始行（0-based，该行及之后视为可清空的数据/示例区） */
export function getListingTemplateDataStartRowIndex(platform) {
  const key = canonicalListingTemplatePlatform(platform);
  const map = {
    'SHEIN半托': 6,
    SHEIN: 6,
    'TEMU半托': 5,
    TEMU: 5,
    店小秘: 1,
    DXM: 1,
    TikTok: 3,
    TIKTOK: 3,
  };
  return map[key] ?? 1;
}

function normalizeSheetToken(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/模板/g, '模版');
}

/** SHEIN 模板中的参考/说明类工作表（非商品编辑主表） */
export const SHEIN_REFERENCE_SHEET_NAMES = new Set([
  '操作指引',
  '有效属性值',
  '有效品牌列表',
  '有效产地列表',
  '尺寸表模板',
  '尺寸表模版',
]);

function isSheinReferenceSheetName(name) {
  const trimmed = String(name || '').trim();
  if (!trimmed) return true;
  if (SHEIN_REFERENCE_SHEET_NAMES.has(trimmed)) return true;
  return false;
}

/** 工作簿中可见工作表名称（忽略 Hidden=1/2 的隐藏表） */
export function getVisibleSheetNames(workbook) {
  const allNames = workbook?.SheetNames || [];
  const wbSheets = workbook?.Workbook?.Sheets;
  if (!Array.isArray(wbSheets) || wbSheets.length !== allNames.length) {
    return allNames;
  }
  return allNames.filter((_, index) => {
    const hidden = wbSheets[index]?.Hidden;
    return hidden !== 1 && hidden !== 2;
  });
}

/**
 * SHEIN 商品模板主工作表：
 * 1. 优先取从左第 2 个 tab（SheetNames[1]），且该 sheet 为可见、非参考表
 * 2. 否则取第一个可见且非参考表的工作表
 */
export function resolveSheinMainSheetName(workbook) {
  const allNames = workbook?.SheetNames || [];
  const visibleSet = new Set(getVisibleSheetNames(workbook));

  if (allNames.length >= 2) {
    const secondTab = allNames[1];
    if (visibleSet.has(secondTab) && !isSheinReferenceSheetName(secondTab)) {
      return secondTab;
    }
  }

  for (const name of allNames) {
    if (!visibleSet.has(name)) continue;
    if (!isSheinReferenceSheetName(name)) return name;
  }

  if (allNames.length >= 2 && visibleSet.has(allNames[1])) return allNames[1];
  if (allNames.length >= 1 && visibleSet.has(allNames[0])) return allNames[0];
  return allNames[1] || allNames[0] || null;
}

/** 读取工作表为二维数组（sheet_to_json + 稀疏单元格合并） */
export function readWorksheetMatrix(XLSX, worksheet) {
  if (!worksheet || !XLSX?.utils) return [];

  const cellToText = (cell) => {
    if (!cell) return '';
    if (cell.w != null && String(cell.w).trim() !== '') return String(cell.w);
    if (cell.v == null) return '';
    if (typeof cell.v === 'object') {
      if (cell.v.text != null) return String(cell.v.text);
      if (Array.isArray(cell.v.richText)) {
        return cell.v.richText.map((part) => part?.t || '').join('');
      }
    }
    return String(cell.v);
  };

  const normalizeRow = (row) => {
    if (!Array.isArray(row)) return [];
    return row.map((cell) => {
      if (cell == null) return '';
      if (typeof cell === 'object') {
        if (cell.text != null) return String(cell.text);
        if (Array.isArray(cell.richText)) {
          return cell.richText.map((part) => part?.t || '').join('');
        }
      }
      return String(cell);
    });
  };

  let jsonData = [];
  try {
    jsonData = XLSX.utils.sheet_to_json(worksheet, {
      header: 1,
      defval: '',
      blankrows: true,
      raw: false,
    }).map(normalizeRow);
  } catch {
    jsonData = [];
  }

  let maxRow = jsonData.length - 1;
  let maxCol = jsonData.reduce((max, row) => Math.max(max, (row?.length || 0) - 1), -1);

  if (worksheet['!ref']) {
    try {
      const range = XLSX.utils.decode_range(worksheet['!ref']);
      maxRow = Math.max(maxRow, range.e.r);
      maxCol = Math.max(maxCol, range.e.c);
    } catch {
      // ignore invalid ref
    }
  }

  Object.keys(worksheet).forEach((key) => {
    if (key[0] === '!') return;
    try {
      const cellAddress = XLSX.utils.decode_cell(key);
      if (cellAddress.r > maxRow) maxRow = cellAddress.r;
      if (cellAddress.c > maxCol) maxCol = cellAddress.c;
    } catch {
      // ignore invalid cell key
    }
  });

  if (maxRow < 0 || maxCol < 0) {
    return jsonData.length > 0 ? jsonData : [];
  }

  const dense = [];
  for (let R = 0; R <= maxRow; R += 1) {
    const sparseRow = jsonData[R];
    const row = [];
    for (let C = 0; C <= maxCol; C += 1) {
      const sparseValue = sparseRow?.[C];
      if (sparseValue != null && String(sparseValue).trim() !== '') {
        row.push(String(sparseValue));
        continue;
      }
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      row.push(cellToText(worksheet[cellAddress]));
    }
    dense.push(row);
  }
  return dense;
}

function updateSheetRefFromCells(XLSX, sheet) {
  if (!sheet) return;
  let maxRow = -1;
  let maxCol = -1;
  Object.keys(sheet).forEach((key) => {
    if (key[0] === '!') return;
    try {
      const { r, c } = XLSX.utils.decode_cell(key);
      if (r > maxRow) maxRow = r;
      if (c > maxCol) maxCol = c;
    } catch {
      // ignore
    }
  });
  if (maxRow >= 0 && maxCol >= 0) {
    sheet['!ref'] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: maxRow, c: maxCol },
    });
  }
}

function pickSheetName(sheetNames, variants) {
  const normalized = (sheetNames || []).map((n) => ({
    raw: n,
    key: normalizeSheetToken(n),
  }));
  const targets = variants.map((v) => normalizeSheetToken(v));
  for (const t of targets) {
    const hit = normalized.find((s) => s.key === t || s.key === t.replace(/模版/g, '模板'));
    if (hit) return hit.raw;
  }
  return null;
}

/** 解析平台对应的主编辑工作表名称 */
export function resolveListingTemplateMainSheetName(platform, sheetNames) {
  const key = canonicalListingTemplatePlatform(platform);
  if (key === 'TEMU半托' || key === 'TEMU') {
    return pickSheetName(sheetNames, ['模版', '模板']);
  }
  if (key === 'SHEIN半托' || key === 'SHEIN') {
    const byName = pickSheetName(sheetNames, ['模版', '模板']);
    if (byName) return byName;
    if (sheetNames?.length >= 2) return sheetNames[1];
    return sheetNames?.[0] || null;
  }
  if (key === '店小秘' || key === 'DXM') {
    return pickSheetName(sheetNames, ['导入模版', '导入模板']);
  }
  if (key === 'TikTok' || key === 'TIKTOK') {
    if (sheetNames?.[0]) return sheetNames[0];
    return pickSheetName(sheetNames, ['Template']);
  }
  return sheetNames?.[0] || null;
}

function clearSheetDataRows(XLSX, sheet, dataStartRow0) {
  if (!sheet?.['!ref']) return 0;
  const range = XLSX.utils.decode_range(sheet['!ref']);
  let clearedRows = 0;
  for (let r = Math.max(dataStartRow0, range.s.r); r <= range.e.r; r++) {
    let rowHadValue = false;
    for (let c = range.s.c; c <= range.e.c; c++) {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (sheet[addr]) {
        delete sheet[addr];
        rowHadValue = true;
      }
    }
    if (rowHadValue) clearedRows += 1;
  }
  updateSheetRefFromCells(XLSX, sheet);
  return clearedRows;
}

/**
 * 清空官方模板中数据区的示例行（保留表头、必填、tooltip 等元数据行）
 * @returns {{ file: File, clearedRowCount: number }}
 */
export async function clearListingTemplateSampleRows(file, platform) {
  if (!file || !platform) {
    return { file, clearedRowCount: 0 };
  }
  const XLSX = await loadListingTemplateXLSX();
  const platformKey = canonicalListingTemplatePlatform(platform);
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, LISTING_TEMPLATE_XLSX_READ_OPTIONS);
  const sheetName = (platformKey === 'SHEIN半托' || platformKey === 'SHEIN')
    ? resolveSheinMainSheetName(workbook)
    : resolveListingTemplateMainSheetName(platformKey, workbook.SheetNames || []);
  if (!sheetName || !workbook.Sheets[sheetName]) {
    return { file, clearedRowCount: 0 };
  }
  const dataStart = getListingTemplateDataStartRowIndex(platformKey);
  const clearedRowCount = clearSheetDataRows(XLSX, workbook.Sheets[sheetName], dataStart);
  const out = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
  const mime =
    file.type ||
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const clearedFile = new File([out], file.name, { type: mime, lastModified: Date.now() });
  return { file: clearedFile, clearedRowCount };
}
